'use client'

import './globals.css'
import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { Providers } from './providers'
import { Header } from '@/components/layout/Header'
import { Sidebar, SidebarProvider, useSidebar } from '@/components/layout/Sidebar'
import { MobileSidebar } from '@/components/layout/MobileSidebar'
import { ActiveRoleProvider } from '@/lib/active-role'
import { cn } from '@/lib/utils'

const AUTH_ROUTES = ['/auth/signin', '/auth/register', '/auth/error']

function isImmersiveViewerRoute(pathname: string | null): boolean {
  if (!pathname) return false
  if (/^\/dpr\/my-dpr\/[^/]+$/.test(pathname)) return true
  if (pathname === '/sourcing/pr-workspace' || pathname.startsWith('/sourcing/pr-workspace/')) {
    return true
  }
  if (pathname === '/pr/new') return true
  return false
}

function LayoutContent({ children }: { children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isCollapsed } = useSidebar()
  const pathname = usePathname()

  const isAuthRoute = AUTH_ROUTES.some((route) => pathname?.startsWith(route)) || !!pathname?.startsWith('/flow/card/')
  const isImmersiveViewer = isImmersiveViewerRoute(pathname)

  if (isAuthRoute) {
    return (
      <div className='relative min-h-screen bg-transparent'>
        <main className='relative z-10 flex min-h-screen items-center justify-center p-4'>
          {children}
        </main>
      </div>
    )
  }

  return (
    <div className='relative min-h-screen bg-transparent'>
      <Sidebar />

      <MobileSidebar
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      <div
        className={cn(
          'relative min-h-screen bg-transparent',
          isCollapsed ? 'md:pl-[72px]' : 'md:pl-[260px]',
          'transition-all duration-300 ease-out'
        )}
      >
        <Header onOpenMobileMenu={() => setMobileMenuOpen(true)} />

        <main
          id='main-content'
          role='main'
          aria-label='Main content'
          className={cn(
            'relative flex flex-col bg-transparent',
            isImmersiveViewer
              ? 'h-screen overflow-hidden pt-24'
              : cn('min-h-screen pt-24', 'px-4 pb-8 lg:gap-6 lg:px-8 lg:pb-8')
          )}
          tabIndex={-1}
        >
          {children}
        </main>
      </div>
    </div>
  )
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <html lang='en' suppressHydrationWarning>
      <head>
        <title>Procurement Command Center | CapEx Tendering & Feasibility Management</title>
        <meta
          name='description'
          content='AI-powered RFP orchestration for smarter procurement decisions'
        />
        <link rel='icon' type='image/png' href='/supervity-favicon.png' />
        <link rel='apple-touch-icon' href='/supervity-favicon.png' />
        <link rel='preconnect' href='https://fonts.googleapis.com' />
        <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin='anonymous' />
      </head>
      <body className='min-h-screen bg-transparent font-sans text-foreground antialiased'>
        <Providers>
          <ActiveRoleProvider>
            <SidebarProvider>
              <LayoutContent>{children}</LayoutContent>
            </SidebarProvider>
          </ActiveRoleProvider>
        </Providers>
      </body>
    </html>
  )
}
